import * as React from "react";
import { FormGroup } from "../RightAsideHelpers";
import { WorkflowNode, ClassificationRoute } from "../../types";
import { TeamSelect } from "@/components/messaging/TeamSelect";
import IconPlus from "@/icons/IconPlus";
import IconClose from "@/icons/IconClose";
import TextArea from "@/components/TextArea";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function ClassifyAndRouteEditor({ node, onUpdate }: Props) {
  const routes: ClassificationRoute[] = Array.isArray(node.classificationRoutes)
    ? node.classificationRoutes
    : [];

  function updateRoute(index: number, patch: Partial<ClassificationRoute>) {
    const updated = routes.map((r, i) => (i === index ? { ...r, ...patch } : r));
    onUpdate({ classificationRoutes: updated });
  }

  function addRoute() {
    onUpdate({ classificationRoutes: [...routes, { label: "", teamId: 0 }] });
  }

  function removeRoute(index: number) {
    onUpdate({ classificationRoutes: routes.filter((_, i) => i !== index) });
  }

  return (
    <>
      <FormGroup
        label="Classification prompt"
        tooltipText="Describe the intent categories you want to detect. The AI will classify the visitor's first message into one of the labels you define below."
      >
        <TextArea
          value={node.classificationPrompt ?? ""}
          onChange={(e) => onUpdate({ classificationPrompt: e.target.value })}
          placeholder={`Example:\nClassify the visitor's message into exactly one of the labels below.\n- billing: questions about invoices, payments, or subscriptions\n- support: technical problems or errors\n- sales: pricing, trials, or purchase inquiries`}
          className="w-full min-h-[120px]"
        />
        <p className="text-xs text-gray-400 mt-1">
          The AI responds with a single label from your list. Keep labels lowercase and unique.
        </p>
      </FormGroup>

      <FormGroup label="Routes">
        {routes.length === 0 && (
          <p className="text-xs text-gray-400 mb-2">
            Add at least one label → team mapping.
          </p>
        )}
        <div className="flex flex-col gap-2">
          {routes.map((route, i) => (
            <div key={i} className="flex gap-2 items-start">
              <input
                type="text"
                className="input flex-1"
                placeholder="label (e.g. billing)"
                value={route.label}
                onChange={(e) => updateRoute(i, { label: e.target.value })}
              />
              <div className="flex-1">
                <TeamSelect
                  value={route.teamId || undefined}
                  onChange={(teamId) => updateRoute(i, { teamId })}
                />
              </div>
              <button
                type="button"
                aria-label="Remove route"
                className="mt-1 text-gray-400 hover:text-red-500"
                onClick={() => removeRoute(i)}
              >
                <IconClose />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="mt-2 flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600"
          onClick={addRoute}
        >
          <IconPlus />
          Add route
        </button>
      </FormGroup>

      <FormGroup
        label="Fallback team"
        tooltipText="Used when the AI cannot classify — for example if the AI service is temporarily unavailable, or if the visitor's message doesn't clearly match any of your labels."
      >
        <TeamSelect
          value={node.classificationFallbackTeamId ?? undefined}
          onChange={(teamId) =>
            onUpdate({ classificationFallbackTeamId: teamId || undefined })
          }
        />
        <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600 leading-relaxed">
          <div className="font-semibold text-slate-700 mb-0.5">What happens when the fallback fires:</div>
          <ul className="list-disc list-inside space-y-0.5">
            <li>AI label didn't match any of your routes above</li>
            <li>AI service was unavailable or timed out</li>
            <li>Prompt is empty or routes aren't configured yet</li>
          </ul>
          <div className="mt-1.5">
            {node.classificationFallbackTeamId
              ? "The visitor is routed to the selected fallback team. No conversation is lost."
              : <span className="text-amber-600 font-medium">No fallback team selected — the visitor will stay on the team this workflow was triggered for. This is safe but may mean unclassified chats land in a general queue. Selecting a fallback team is recommended.</span>
            }
          </div>
        </div>
      </FormGroup>
    </>
  );
}
